import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useIssues } from "@/lib/useIssues";
import { IssuesBanner } from "@/components/IssuesBanner";
import { DelistedAlert } from "@/components/DelistedAlert";
import { SplitsNotice } from "@/components/SplitsNotice";
import { Bell, ChevronDown } from "lucide-react";

/** One slim bar summarising Holdings notices; expands to the full issue / no-price / split panels. */
export function HoldingsNotices() {
    const [open, setOpen] = useState(false);
    const issues = useIssues().data?.count || 0;
    // same query keys as the panels, so data is shared, not fetched twice
    const delisted = useQuery({ queryKey: ["delisted"], queryFn: async () => (await api.get("/corporate-actions/delisted")).data }).data?.delisted?.length || 0;
    const splits = (useQuery({ queryKey: ["splits"], queryFn: async () => (await api.get("/corporate-actions/splits")).data }).data?.detected || [])
        .filter((d) => !d.acknowledged).length;
    const parts = [
        issues && { n: issues, text: `open issue${issues > 1 ? "s" : ""}`, cls: "text-loss" },
        delisted && { n: delisted, text: `without a live price`, cls: "text-loss" },
        splits && { n: splits, text: `split${splits > 1 ? "s" : ""} applied`, cls: "text-primary" },
    ].filter(Boolean);
    if (!parts.length) return null;
    return (
        <div className="space-y-2" data-testid="holdings-notices">
            <button onClick={() => setOpen((o) => !o)} data-testid="holdings-notices-toggle"
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2 text-sm hover:bg-muted/40 transition-colors">
                <span className="flex items-center gap-2 flex-wrap text-left">
                    <Bell className="w-4 h-4 text-primary shrink-0" />
                    {parts.map((p, i) => (
                        <span key={p.text} className="whitespace-nowrap"><b className={p.cls}>{p.n}</b> {p.text}{i < parts.length - 1 && <span className="text-muted-foreground ml-2">·</span>}</span>
                    ))}
                </span>
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">{open ? "Hide" : "Details"}<ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
            </button>
            {open && (
                <div className="space-y-2" data-testid="holdings-notices-details">
                    <IssuesBanner />
                    <DelistedAlert />
                    <SplitsNotice />
                </div>
            )}
        </div>
    );
}
